import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Save, Loader2, Clock, CheckCircle2, AlertCircle, Check, X, User, MapPin, Users, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DashboardShell, StatCard } from "@/components/DashboardShell";
import { ComingSoon } from "@/components/ComingSoon";
import { CountrySelect, PhoneField } from "@/components/CountryPhone";
import { PhotoUpload } from "@/components/PhotoUpload";
import { PartnerAccountability } from "@/components/PartnerAccountability";
import { PartnerWallet } from "@/components/PartnerWallet";
import { COUNTRY_BY_CODE, localFromE164 } from "@/data/countries";
import { SPORTS, SPORT_ICONS } from "@/data/sports";
import { AVAILABILITY_OPTIONS, PARTNER_MODES } from "@/data/matching";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function PartnerDashboard() {
  const { user, refreshUser } = useAuth();
  const p = user?.profile || {};
  const status = user?.partner_status || "pending";
  const [photo, setPhoto] = useState(p.photo || "");
  const [bio, setBio] = useState(p.bio || "");
  const [sports, setSports] = useState(p.sports || []);
  const [availability, setAvailability] = useState(p.availability || []);
  const [mode, setMode] = useState(p.accountability_mode || "Both");
  const [country, setCountry] = useState(COUNTRY_BY_CODE[user?.country_code] || null);
  const [phone, setPhone] = useState(localFromE164(user?.phone, user?.dial_code));
  const [state, setState] = useState(p.state || "");
  const [city, setCity] = useState(p.city || "");
  const [saving, setSaving] = useState(false);

  const [requests, setRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [acting, setActing] = useState(null);

  const loadMatches = useCallback(async () => {
    if (status !== "approved") return;
    try {
      const [r, m] = await Promise.all([api.get("/matches/requests/incoming"), api.get("/partner/members")]);
      setRequests(r.data.requests);
      setMembers(m.data.members);
    } catch { /* ignore */ }
  }, [status]);

  useEffect(() => { loadMatches(); }, [loadMatches]);

  const toggleSport = (slug) => setSports((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  const toggleAvail = (a) => setAvailability((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { bio, sports, availability, state, city, photo, accountability_mode: mode };
      if (country) {
        payload.country = country.name; payload.country_code = country.code; payload.dial_code = country.dial;
        payload.phone = phone.trim() ? `${country.dial}${phone.replace(/\D/g, "").replace(/^0+/, "")}` : "";
      }
      await api.put("/profile", payload);
      await refreshUser();
      toast.success("Profile saved!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const respond = async (id, action) => {
    setActing(id);
    try {
      await api.post(`/matches/${id}/${action}`);
      toast.success(action === "accept" ? "Match accepted — member added." : "Request declined.");
      await loadMatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const endWithMember = async (id) => {
    setActing(id);
    try {
      await api.post(`/matches/${id}/end`);
      toast.success("Relationship ended.");
      await loadMatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const statusMeta = {
    pending: { Icon: AlertCircle, text: "Pending Approval", cls: "text-amber-400 bg-amber-400/10", note: "Your partner account is under review. You can set up your profile now — members can find you once approved." },
    approved: { Icon: CheckCircle2, text: "Approved", cls: "text-green-400 bg-green-400/10", note: "You're live! Members can discover you and send match requests." },
    rejected: { Icon: AlertCircle, text: "Not Approved", cls: "text-[#E53E3E] bg-[#E53E3E]/10", note: "Your application was not approved. Contact support for details." },
  }[status];

  return (
    <DashboardShell testid="partner-dashboard">
      <div className="mb-6" data-testid="partner-dashboard-header">
        <span className="text-xs font-bold uppercase tracking-widest text-[#E53E3E]">Accountability Partner</span>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">Welcome, {user?.name?.split(" ")[0]}</h1>
      </div>

      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-3 ${statusMeta.cls}`} data-testid="partner-status-badge">
        <statusMeta.Icon className="w-4 h-4" /> {statusMeta.text}
      </div>
      <p className="text-sm text-zinc-400 mb-8">{statusMeta.note}</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Assigned Members" value={members.length} hint="Active" />
        <StatCard label="Pending Requests" value={requests.length} hint="Awaiting response" />
        <StatCard label="Sports" value={sports.length} hint="Supported" />
        <StatCard label="Mode" value={mode || "—"} hint="Accountability" />
      </div>

      {status === "approved" && <PartnerAccountability />}
      {status === "approved" && <PartnerWallet />}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight">Partner Profile</h2>
          <p className="text-sm text-zinc-500 mt-1">Complete your profile so members can find and match with you.</p>

          <div className="mt-6"><PhotoUpload value={photo} onChange={setPhoto} testid="partner-photo" /></div>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-zinc-300">Country</Label>
              <CountrySelect value={country?.code} onChange={setCountry} testid="partner-country-select" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Phone / WhatsApp</Label>
              <PhoneField dialCode={country?.dial} value={phone} onChange={setPhone} testid="partner-phone-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">State / Province / Region</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. Lagos" className="h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="partner-state-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Ikeja" className="h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E]" data-testid="partner-city-input" />
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <Label className="text-zinc-300">Short bio</Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Share your coaching style and experience..." className="bg-zinc-900 border-zinc-800 focus-visible:ring-[#E53E3E] min-h-[90px]" data-testid="partner-bio-input" />
          </div>

          <div className="mt-6">
            <Label className="text-zinc-300">Sports you support</Label>
            <div className="mt-3 flex flex-wrap gap-2">
              {SPORTS.map((s) => {
                const Icon = SPORT_ICONS[s.slug];
                const active = sports.includes(s.slug);
                return (
                  <button key={s.slug} onClick={() => toggleSport(s.slug)} data-testid={`partner-sport-${s.slug}`}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#E53E3E] border-[#E53E3E] text-white" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                    <Icon className="w-4 h-4" /> {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-zinc-300">Availability</Label>
              <div className="mt-3 flex flex-wrap gap-2">
                {AVAILABILITY_OPTIONS.map((a) => {
                  const active = availability.includes(a);
                  return (
                    <button key={a} onClick={() => toggleAvail(a)} data-testid={`partner-avail-${a.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#E53E3E] border-[#E53E3E] text-white" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                      <Clock className="w-4 h-4" /> {a}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Accountability mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-11 bg-zinc-900 border-zinc-800" data-testid="partner-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {PARTNER_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="mt-7 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-12 px-7" data-testid="partner-save-profile-button">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Profile</>}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Match Requests */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="partner-requests-panel">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Inbox className="w-5 h-5 text-[#E53E3E]" /> Match Requests</h2>
            {status !== "approved" ? (
              <p className="mt-3 text-sm text-zinc-500">Available once your account is approved.</p>
            ) : requests.length === 0 ? (
              <p className="mt-3 text-sm text-zinc-500" data-testid="partner-no-requests">No pending requests right now.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {requests.map((req) => (
                  <div key={req.id} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4" data-testid={`partner-request-${req.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden border border-zinc-700 bg-zinc-800 grid place-items-center shrink-0">
                        {req.member.photo ? <img src={req.member.photo} alt={req.member.display_name} className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-zinc-500" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{req.member.display_name}</p>
                        <p className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {[req.member.city, req.member.country].filter(Boolean).join(", ") || "—"}</p>
                      </div>
                    </div>
                    {req.member.sports?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{req.member.sports.map((s) => <span key={s} className="px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-300 capitalize">{s.replace("-", " ")}</span>)}</div>}
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" onClick={() => respond(req.id, "accept")} disabled={acting === req.id} className="flex-1 bg-green-600 hover:bg-green-700 rounded-full" data-testid={`partner-accept-${req.id}`}>
                        {acting === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Accept</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => respond(req.id, "decline")} disabled={acting === req.id} className="flex-1 border-zinc-700 bg-transparent hover:bg-[#E53E3E] hover:text-white rounded-full" data-testid={`partner-decline-${req.id}`}>
                        <X className="w-4 h-4 mr-1" /> Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Your Members */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="partner-members-panel">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Users className="w-5 h-5 text-[#E53E3E]" /> Your Members</h2>
            {status !== "approved" ? (
              <p className="mt-3 text-sm text-zinc-500">Available once approved.</p>
            ) : members.length === 0 ? (
              <p className="mt-3 text-sm text-zinc-500" data-testid="partner-no-members">No active members yet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {members.map((m) => (
                  <div key={m.id} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4" data-testid={`partner-member-${m.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden border border-zinc-700 bg-zinc-800 grid place-items-center shrink-0">
                        {m.member.photo ? <img src={m.member.photo} alt={m.member.display_name} className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-zinc-500" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold truncate">{m.member.display_name}</p>
                        <p className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {[m.member.city, m.member.country].filter(Boolean).join(", ") || "—"}</p>
                      </div>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="mt-3 w-full border-zinc-700 bg-transparent hover:bg-[#E53E3E] hover:text-white rounded-full" data-testid={`partner-end-${m.id}`}>End Relationship</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0A0A0B] border-zinc-800">
                        <AlertDialogHeader>
                          <AlertDialogTitle>End relationship with {m.member.display_name}?</AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">The member will be free to find a new partner. History is preserved.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-zinc-700 hover:bg-zinc-800">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => endWithMember(m.id)} className="bg-[#E53E3E] hover:bg-[#C53030]" data-testid={`partner-end-confirm-${m.id}`}>End</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </div>

          <ComingSoon title="Earnings & Rewards" description="Earn as your members stay consistent." />
        </div>
      </div>
    </DashboardShell>
  );
}
